import * as React from "react";
import queryString from "query-string";
import { useLocation, useHistory } from "react-router-dom";
import { AclContext } from "../../../../../../contexts/AclContext";
import {
    ClientStatus,
    IACL,
    IAppointmentChecks,
    IAppointmentHeldFields,
    IAppointmentSettingFields,
    IClient,
    IClientBooking,
    ICommunicationSummary,
    IFeeSelect,
    IFeeSettingFields,
    IFollowUpFields,
    IIncompleteInquiryFields,
    IInquiryFields,
    IInsuranceFollowUpFields,
    IInsuranceVerificationFields,
    InsuranceCard,
    IUpdateFeesDetails,
} from "intake_shared/src/types";
import { TextArea } from "../../../../bits/TextArea/TextArea";
import { TSaveClient } from "../../../clientUiTypes";
import { ClientStatusLabels } from "../../../../../../utils/labels";
import InitialInquiryForm from "../InitialInquiryForm/InitialInquiryForm";
import { calculateFees } from "intake_shared/src/fees";
import EditClientOptions from "../EditClientOptions/EditClientOptions";
import NewInitialInquiryForm from "../../new-client/NewInitialInquiry";
import styles from "./EditClient.module.scss";
import InsuranceVerification from "../../insurance/InsuranceVerification/InsuranceVerification";
import FollowUp from "../../../appointments/FollowUp/FollowUp";
import CommsWrapper from "../../../communication/CommsWrapper/CommsWrapper";
import SupportTickets from "../../support-tickets/SupportTickets/SupportTickets";
import { httpsCallable } from "firebase/functions";
import { fxs } from "../../../../../../utils/firestore";
import AppointmentSetting from "../../../appointments/AppointmentSetting/AppointmentSetting";
import AppointmentHistory from "../../../appointments/AppointmentHistory/AppointmentHistory";
import AppointmentHeld from "../../../appointments/AppoitmentHeld/AppointmentHeld";
import AppointmentConfirmed from "../../../appointments/AppointmentConfirmed/AppointmentConfirmed";
import { trackUserActivity } from "../../../../../../store/chart_lock";
import { useChartLock } from "../../../../../../contexts/ChartLockContext";
import FeeSetting from "../../fees/FeeSetting/FeeSetting";
import IntakeSessionHistory from "../../../appointments/AppointmentHistory/IntakeSessionHistory";
import store from "../../../../../../store";
import phone from "phone";

interface EditClientProps {
    clientProp: IClient;
    onSaveClient: TSaveClient;
    activeTab?: ClientStatus | "ALL";
    isInactiveClient?: boolean;
    locked?: boolean;
}

const guardianChartFields = new Set<keyof IClient>([
    "primary_parent",
    "primary_parent_last_name",
    "other_parent",
    "other_parent_last_name",
    "other_parent_email",
]);

const defaultPermission = {
    copay: false,
    billing: false,
    insurance_verification: false,
    intake: false,
    super_admin: false,
};

function getStatusesToShow(permissions: IACL = defaultPermission): ClientStatus[] {
    const allStatuses: ClientStatus[] = [
        ClientStatus.INCOMPLETE_INQUIRY,
        ClientStatus.INQUIRY,
        ClientStatus.INS_VERIFICATION,
        ClientStatus.INS_FOLLOWUP,
        ClientStatus.FEE_SETTING,
        ClientStatus.FOLLOWUP,
        ClientStatus.APPT_SETTING,
        ClientStatus.APPT_HELD,
        ClientStatus.APPT_CONFIRMED,
    ];

    const permissionStatuses: ClientStatus[] = [];

    if (permissions.copay) {
        permissionStatuses.push(ClientStatus.FEE_SETTING);
    }

    if (
        permissions.billing ||
        permissions.insurance_verification ||
        permissions.intake ||
        permissions.super_admin
    ) {
        permissionStatuses.push(...allStatuses);
    }

    return Array.from(new Set(permissionStatuses));
}

const ActivityTrackingNavLink = ({
    eventKey,
    setActiveKey,
    clientId,
    children,
    className,
    onTabChange,
    ...props
}: {
    eventKey: string;
    setActiveKey: (key: string) => void;
    clientId?: string;
    children: React.ReactNode;
    className?: string;
    onTabChange?: (key: string) => void;
    [key: string]: any;
}) => {
    const handleSelect = (key: string | null) => {
        setActiveKey(key || "");
        // Track user activity when switching tabs
        if (clientId) {
            trackUserActivity(clientId);
        }
    };

    return (
        <div
            className={className}
            onClick={() => {
                handleSelect(eventKey);
                onTabChange?.(eventKey);
            }}
            {...props}
        >
            {children}
        </div>
    );
};

type StatusTab = {
    key: string;
    title: React.ReactNode;
    component: React.ReactNode;
    show?: boolean;
};

export default function EditClient({
    clientProp,
    onSaveClient,
    activeTab,
    isInactiveClient = false,
    locked,
}: EditClientProps) {
    const [client, setClient] = React.useState(clientProp);
    const [dirtyGuardianFields, setDirtyGuardianFields] = React.useState(
        new Set<keyof IClient>()
    );
    const [tickets, setTickets] = React.useState<any>();
    const [ticketsLoading, setTicketsLoading] = React.useState(false);
    const [communicationSummary, setCommunicationSummary] =
        React.useState<ICommunicationSummary>();
    const confirmedBookings = client?.confirmed_bookings?.filter((item) => item.id);
    const location = useLocation();
    const history = useHistory();
    // Chart lock functionality
    const { autoLockChart, subscribeToChartLock, unsubscribeFromChartLock } =
        useChartLock();
    const queryParams = queryString.parse(location.search);
    const aclContext = React.useContext(AclContext);
    const userAcl = aclContext?.authUser?.acl || defaultPermission;
    const canViewCommunication = Boolean(userAcl.intake || userAcl.super_admin);
    const clientPhone = phone(client.phone_no ?? "");
    const normalizedClientPhone = clientPhone.isValid ? clientPhone.phoneNumber : "";
    const allowedStatuses = getStatusesToShow(userAcl);
    const canEditInsuranceFollowupNotes = allowedStatuses.includes(
        ClientStatus.INS_FOLLOWUP
    );

    const switchToTab =
        (queryParams.activeTab as string) ||
        (activeTab === "ALL" ? ClientStatus.INQUIRY : activeTab) ||
        client.status;

    const getValidActiveKey = (tab: string) => {
        if (allowedStatuses.includes(tab as ClientStatus)) {
            return tab;
        }
        return allowedStatuses[0] || ClientStatus.INQUIRY;
    };

    const [activeKey, setActiveKey] = React.useState<string>(
        getValidActiveKey(switchToTab)
    );

    const exportGetSupportTickets = httpsCallable(
        fxs,
        "httpFunctionExportGetSupportTickets"
    );

    const getSupportTickets = async () => {
        setTicketsLoading(true);
        const results = await exportGetSupportTickets(client.email || "");
        setTickets(results.data);
        setTicketsLoading(false);
    };

    React.useEffect(() => {
        getSupportTickets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        let isCurrentClient = true;

        if (!canViewCommunication || !normalizedClientPhone) {
            setCommunicationSummary(undefined);
            return;
        }

        store
            .getCommunicationSummary(client.id, normalizedClientPhone)
            .then((summary) => {
                if (isCurrentClient) {
                    setCommunicationSummary(summary);
                }
            })
            .catch(() => {
                if (isCurrentClient) {
                    setCommunicationSummary(undefined);
                }
            });

        return () => {
            isCurrentClient = false;
        };
    }, [canViewCommunication, client.id, normalizedClientPhone]);

    React.useEffect(() => {
        setClient(clientProp);
        setDirtyGuardianFields(new Set<keyof IClient>());
    }, [clientProp]);

    // Subscribe to chart lock changes
    React.useEffect(() => {
        if (client.id) {
            subscribeToChartLock(client.id);
        }

        return () => {
            if (client.id) {
                unsubscribeFromChartLock();
            }
        };
    }, [client.id, subscribeToChartLock, unsubscribeFromChartLock]);

    const validateFees = (inputFee = "", label = "", department_fee_type = "") => {
        const fees = calculateFees(inputFee, label, department_fee_type);
        let message = "";
        if (fees) {
            // Handle single psych fee format (e.g., "16" or "16.09")
            if (fees.single_psych_fee) {
                return `Client pays $${fees.single_psych_fee}/session`;
            }

            // Handle complex fee structure for Psych Fee (evaluation + UDM + ongoing)
            if (fees.evaluation_fee || fees.udm_fee || fees.ongoing_fee) {
                // Handle UDM + ongoing format (no evaluation) - e.g., "452 UDM, then 22"
                if (fees.udm_fee && fees.ongoing_fee && !fees.evaluation_fee) {
                    return `$${fees.udm_fee}/session until your deductible is met and $${fees.ongoing_fee}/session for ongoing sessions`;
                }

                if (fees.evaluation_fee) {
                    // Check if evaluation has multiple sessions (like 165.76x2)
                    const evaluation_match = fees.evaluation_fee.match(
                        /^(\d+(?:\.\d+)?)x(\d+)$/
                    );
                    if (evaluation_match) {
                        const evalAmount = evaluation_match[1];
                        const evalSessions = evaluation_match[2];

                        // If x1, treat as single evaluation
                        if (evalSessions === "1") {
                            message += `Fee schedule is $${evalAmount} for your evaluation `;
                        } else {
                            // If x2 or higher, treat as multiple sessions
                            message += `Fee schedule is $${evalAmount}/session for your first ${evalSessions} sessions `;
                        }
                    } else {
                        message += `Fee schedule is $${fees.evaluation_fee} for your evaluation `;
                    }
                }
                if (fees.udm_fee) {
                    message += `and $${fees.udm_fee}/session until your deductible is met `;
                }
                if (fees.ongoing_fee) {
                    message += `and $${fees.ongoing_fee}/session for ongoing sessions `;
                }
                return message.trim();
            }

            // Handle existing fee structures
            const has_temp_fee = fees.temp_fee && fees.temp_no_of_sessions;
            if (has_temp_fee) {
                message += `Fee schedule is $${fees.temp_fee}/session for the first ${fees.temp_no_of_sessions} sessions `;
            }

            if (fees.single_fee) {
                message += `${has_temp_fee ? "then" : "Fee schedule is"} $${
                    fees.single_fee
                }/session `;
            }

            if (fees.tbd_fee) {
                message += `${has_temp_fee ? "then" : "Fee schedule is"} $${
                    fees.tbd_fee
                }/session until their insurance company responds to our first claim `;
            }
        }

        return message;
    };

    const updateClient = React.useCallback(
        (
            label: keyof IClient,
            value:
                | string
                | Date
                | ClientStatus
                | boolean
                | IAppointmentChecks
                | IFeeSelect
                | IUpdateFeesDetails
                | IClientBooking
                | IClientBooking[]
        ) => {
            // Auto-lock chart when user starts editing
            if (client.id) {
                autoLockChart(client.id);
                // Track user activity to prevent inactivity timeout
                trackUserActivity(client.id);
            }
            if (guardianChartFields.has(label)) {
                setDirtyGuardianFields((prev) => new Set(prev).add(label));
            }
            setClient((prev) => ({ ...prev, [label]: value }));
        },
        [autoLockChart, client.id]
    );

    const updateClientByStatus = (
        status:
            | ClientStatus.INS_VERIFICATION
            | ClientStatus.FEE_SETTING
            | ClientStatus.INS_FOLLOWUP
            | ClientStatus.APPT_SETTING
            | ClientStatus.INQUIRY
            | ClientStatus.INCOMPLETE_INQUIRY
            | ClientStatus.FOLLOWUP
            | ClientStatus.APPT_HELD,
        label:
            | keyof IIncompleteInquiryFields
            | keyof IInsuranceVerificationFields
            | keyof IInsuranceFollowUpFields
            | keyof IFeeSettingFields
            | keyof IAppointmentSettingFields
            | keyof IInquiryFields
            | keyof IFollowUpFields
            | keyof IAppointmentHeldFields,
        value: string | boolean | InsuranceCard[] | Date
    ) => {
        // Track user activity to prevent inactivity timeout
        if (client.id) {
            trackUserActivity(client.id);
        }
        setClient((prev) => {
            const updatedClient = { ...prev };
            updatedClient[status] = {
                ...prev[status],
                [label]: value,
            } as any;
            if (
                [
                    "dr_fee",
                    "dr_fee_a",
                    "dr_fee_b",
                    "dr_fee_c",
                    "masters_fee",
                    "inn_masters_fee",
                    "inn_nutrition_fee",
                    "psychiatry_fee",
                    "psychiatry_fee_a",
                    "psychiatry_fee_b",
                    "psychiatry_fee_c",
                    "intern_fee",
                    "lic_fee",
                    "kap_fee",
                    "nutrition_fee",
                    "lic_a_fee",
                    "lic_b_fee",
                    "lic_c_fee",
                    "extern_fee",
                    "career_fee",
                    "gifted_assessment_fee",
                    "short_term_evaluation_documentation_fee",
                ].includes(label)
            ) {
                // Determine department_fee_type based on the fee label
                const department_fee_type = [
                    "inn_masters_fee",
                    "inn_nutrition_fee",
                ].includes(label)
                    ? "INN FEES"
                    : "";
                const fee_calc = validateFees(
                    value as string,
                    label as string,
                    department_fee_type
                );
                (updatedClient[status] as any)[`${label}_calc`] = fee_calc;
            }
            return updatedClient;
        });
    };

    const updateMultipleClientFieldsByStatus = (
        status:
            | ClientStatus.INS_VERIFICATION
            | ClientStatus.FEE_SETTING
            | ClientStatus.INS_FOLLOWUP
            | ClientStatus.APPT_SETTING
            | ClientStatus.INQUIRY
            | ClientStatus.INCOMPLETE_INQUIRY
            | ClientStatus.APPT_HELD,
        updatedFields: {
            [key: string]: string | Date;
        }
    ) => {
        setClient((prev) => {
            const updatedClient = { ...prev };
            updatedClient[status] = {
                ...prev[status],
                ...updatedFields,
            } as any;

            Object.keys(updatedFields).forEach((label) => {
                const fieldValue = updatedFields[label];
                if (
                    typeof fieldValue === "string" &&
                    [
                        "dr_fee",
                        "dr_fee_a",
                        "dr_fee_b",
                        "dr_fee_c",
                        "masters_fee",
                        "inn_masters_fee",
                        "inn_nutrition_fee",
                        "psychiatry_fee",
                        "psychiatry_fee_a",
                        "psychiatry_fee_b",
                        "psychiatry_fee_c",
                        "intern_fee",
                        "lic_fee",
                        "kap_fee",
                        "nutrition_fee",
                        "lic_a_fee",
                        "lic_b_fee",
                        "lic_c_fee",
                        "extern_fee",
                        "career_fee",
                        "gifted_assessment_fee",
                        "short_term_evaluation_documentation_fee",
                    ].includes(label)
                ) {
                    // Determine department_fee_type based on the fee field
                    const department_fee_type = [
                        "inn_masters_fee",
                        "inn_nutrition_fee",
                    ].includes(label)
                        ? "INN FEES"
                        : "";
                    const fee_calc = validateFees(
                        fieldValue,
                        label,
                        department_fee_type
                    );
                    (updatedClient[status] as any)[`${label}_calc`] = fee_calc;
                }
            });

            return updatedClient;
        });
    };

    const getCountClassName = (tabKey: string) => {
        return `${styles.count} ${
            activeKey === tabKey ? styles.tabItemActiveCount : ""
        }`;
    };

    const handleCommunicationSummaryChange = React.useCallback(
        (summary: ICommunicationSummary) => {
            setCommunicationSummary((currentSummary) => {
                if (currentSummary?.phone_number !== summary.phone_number) {
                    return summary;
                }
                return { ...currentSummary, ...summary };
            });
        },
        []
    );

    const statusTabs: StatusTab[] = [
        {
            key: ClientStatus.INCOMPLETE_INQUIRY,
            title: "Incomplete Inquiry",
            component:
                queryParams.client_type === "new_client" ? (
                    <NewInitialInquiryForm
                        updateClient={updateClient}
                        newClientData={client}
                    />
                ) : (
                    <InitialInquiryForm
                        onSaveClient={onSaveClient}
                        client={client}
                        updateClient={updateClient}
                        updateClientByStatus={updateClientByStatus}
                        updateMultipleClientFieldsByStatus={
                            updateMultipleClientFieldsByStatus
                        }
                    />
                ),
            show: allowedStatuses.includes(ClientStatus.INCOMPLETE_INQUIRY),
        },
        {
            key: ClientStatus.INQUIRY,
            title: "Initial Inquiry",
            component: (
                <InitialInquiryForm
                    onSaveClient={onSaveClient}
                    client={client}
                    updateClient={updateClient}
                    updateClientByStatus={updateClientByStatus}
                    updateMultipleClientFieldsByStatus={
                        updateMultipleClientFieldsByStatus
                    }
                />
            ),
            show: allowedStatuses.includes(ClientStatus.INQUIRY),
        },
        {
            key: ClientStatus.INS_VERIFICATION,
            title: "Insurance Verification",
            component: (
                <InsuranceVerification
                    client={client}
                    updateClient={updateClient}
                    updateClientByStatus={updateClientByStatus}
                    updateMultipleClientFieldsByStatus={
                        updateMultipleClientFieldsByStatus
                    }
                />
            ),
            show: allowedStatuses.includes(ClientStatus.INS_VERIFICATION),
        },
        {
            key: ClientStatus.INS_FOLLOWUP,
            title: "Insurance Follow-up",
            component: (
                <InitialInquiryForm
                    onSaveClient={onSaveClient}
                    client={client}
                    updateClient={updateClient}
                    updateClientByStatus={updateClientByStatus}
                    updateMultipleClientFieldsByStatus={
                        updateMultipleClientFieldsByStatus
                    }
                />
            ),
            show: allowedStatuses.includes(ClientStatus.INS_FOLLOWUP),
        },
        {
            key: ClientStatus.FEE_SETTING,
            title: ClientStatusLabels["Fee Setting"],
            component: (
                <FeeSetting
                    client={client}
                    updateClient={updateClient}
                    updateClientByStatus={updateClientByStatus}
                    updateMultipleClientFieldsByStatus={
                        updateMultipleClientFieldsByStatus
                    }
                />
            ),
            show: allowedStatuses.includes(ClientStatus.FEE_SETTING),
        },
        {
            key: ClientStatus.FOLLOWUP,
            title: "Followup",
            component: <FollowUp client={client} onSaveClient={onSaveClient} />,
            show: allowedStatuses.includes(ClientStatus.FOLLOWUP),
        },
        {
            key: ClientStatus.APPT_SETTING,
            title: "Appointment Setting",
            component: (
                <AppointmentSetting
                    client={client}
                    updateClient={updateClient}
                    updateClientByStatus={updateClientByStatus}
                    updateMultipleClientFieldsByStatus={
                        updateMultipleClientFieldsByStatus
                    }
                    onSaveClient={onSaveClient}
                />
            ),
            show: allowedStatuses.includes(ClientStatus.APPT_SETTING),
        },
        {
            key: ClientStatus.APPT_HELD,
            title: (
                <>
                    Appointments Held{" "}
                    {client?.held_bookings?.length ? (
                        <span className={getCountClassName(ClientStatus.APPT_HELD)}>
                            {client?.held_bookings?.length}
                        </span>
                    ) : (
                        <span className={getCountClassName(ClientStatus.APPT_HELD)}>
                            0
                        </span>
                    )}
                </>
            ),
            component: (
                <AppointmentHeld
                    initialHistories={client.appointment_history}
                    client={client}
                    updateClient={updateClient}
                    updateClientByStatus={updateClientByStatus}
                    onSaveClient={onSaveClient}
                />
            ),
            show: allowedStatuses.includes(ClientStatus.APPT_HELD),
        },
        {
            key: ClientStatus.APPT_CONFIRMED,
            title: (
                <>
                    Appointments Confirmed{" "}
                    {confirmedBookings?.length ? (
                        <span
                            className={getCountClassName(
                                ClientStatus.APPT_CONFIRMED
                            )}
                        >
                            {confirmedBookings?.length}
                        </span>
                    ) : (
                        <span
                            className={getCountClassName(
                                ClientStatus.APPT_CONFIRMED
                            )}
                        >
                            0
                        </span>
                    )}
                </>
            ),
            component: (
                <AppointmentConfirmed
                    client={client}
                    updateClient={updateClient}
                    initialHistories={client.appointment_history}
                    updateClientByStatus={updateClientByStatus}
                    onSaveClient={onSaveClient}
                />
            ),
            show: allowedStatuses.includes(ClientStatus.APPT_CONFIRMED),
        },
    ];

    const additionalTabs: StatusTab[] = [];

    if (userAcl.intake || userAcl.super_admin) {
        additionalTabs.push(
            {
                key: "communication",
                title: (
                    <span className={styles.communicationTabTitle}>
                        <span>Communication</span>
                        {communicationSummary ? (
                            <span className={styles.communicationCounts}>
                                {communicationSummary.sms_count !== undefined ? (
                                    <span>
                                        SMS
                                        <span
                                            className={getCountClassName(
                                                "communication"
                                            )}
                                        >
                                            {communicationSummary.sms_count}
                                        </span>
                                    </span>
                                ) : null}
                                {communicationSummary.call_count !== undefined ? (
                                    <span>
                                        Calls
                                        <span
                                            className={getCountClassName(
                                                "communication"
                                            )}
                                        >
                                            {communicationSummary.call_count}
                                            {communicationSummary.call_count_truncated
                                                ? "+"
                                                : null}
                                        </span>
                                    </span>
                                ) : null}
                            </span>
                        ) : null}
                    </span>
                ),
                component: (
                    <CommsWrapper
                        client_id={client.id}
                        client={client}
                        communicationSummary={communicationSummary}
                        onSummaryChange={handleCommunicationSummaryChange}
                    />
                ),
                show: true,
            },
            {
                key: "tickets",
                title: (
                    <>
                        Support Tickets
                        {tickets?.length ? (
                            <span className={getCountClassName("tickets")}>
                                {tickets?.length}
                            </span>
                        ) : (
                            <span className={getCountClassName("tickets")}>0</span>
                        )}
                    </>
                ),
                component: (
                    <SupportTickets
                        tickets={tickets}
                        client={client}
                        ticketsLoading={ticketsLoading}
                    />
                ),
                show: true,
            }
        );
    }

    if (client.on_waitlist) {
        additionalTabs.push({
            key: "waitlist",
            title: "Waitlist",
            component: <div>Client Waitlist Details</div>,
            show: true,
        });
    }

    const statusTabsToShow = [...statusTabs].filter((t) => t.show);

    const additionalTabsToShow = [...additionalTabs].filter((t) => t.show);

    const showEditOptions = activeKey !== "communication" && activeKey !== "tickets";

    const handleTabChange = (key: string) => {
        const updatedQuery = {
            ...queryParams,
            activeTab: key,
        };
        history.push(`${location.pathname}?${queryString.stringify(updatedQuery)}`);
        setActiveKey(key);
    };

    const getActiveComponent = () => {
        const statusTab = statusTabsToShow.find((tab) => tab.key === activeKey);
        const additionalTab = additionalTabsToShow.find(
            (tab) => tab.key === activeKey
        );
        return statusTab?.component || additionalTab?.component || null;
    };

    const getCurrentActiveTabStatus = () => {
        const statusTab = statusTabsToShow.find((tab) => tab.key === activeKey);
        return statusTab ? (statusTab.key as ClientStatus) : null;
    };

    return (
        <fieldset
            disabled={locked}
            style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
        >
            <div className={styles.layoutWrapper}>
                <div className={styles.sidebar}>
                    <div className={styles.card}>
                        <div className={styles.tabGroup}>
                            {statusTabsToShow.map((tab) => (
                                <ActivityTrackingNavLink
                                    key={tab.key}
                                    eventKey={tab.key}
                                    setActiveKey={setActiveKey}
                                    clientId={client.id}
                                    onTabChange={handleTabChange}
                                    className={`${styles.tabItem} ${
                                        activeKey === tab.key
                                            ? styles.tabItemActive
                                            : ""
                                    }`}
                                >
                                    <div>{tab.title}</div>
                                </ActivityTrackingNavLink>
                            ))}
                        </div>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.tabGroup}>
                            {additionalTabsToShow.map((tab) => (
                                <ActivityTrackingNavLink
                                    key={tab.key}
                                    eventKey={tab.key}
                                    setActiveKey={setActiveKey}
                                    clientId={client.id}
                                    onTabChange={handleTabChange}
                                    className={`${styles.tabItem} ${
                                        activeKey === tab.key
                                            ? styles.tabItemActive
                                            : ""
                                    }`}
                                >
                                    <div>{tab.title}</div>
                                </ActivityTrackingNavLink>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={styles.mainContent}>
                    {showEditOptions && (
                        <div className={styles.editClientOptions}>
                            <EditClientOptions
                                key={`tapPill-editclientoption`}
                                acl={aclContext?.userAcl}
                                client={client}
                                dirtyGuardianFields={dirtyGuardianFields}
                                isInactiveClient={isInactiveClient}
                                allStatusesToShow={allowedStatuses}
                                onSaveClient={onSaveClient}
                                setActiveKey={setActiveKey}
                                currentActiveTabStatus={getCurrentActiveTabStatus()}
                            />
                        </div>
                    )}
                    {getActiveComponent()}
                </div>

                {showEditOptions && (
                    <div className={styles.notesSection}>
                        {activeKey === ClientStatus.APPT_SETTING &&
                            client.appointment_history.length > 0 && (
                                <AppointmentHistory
                                    initialHistories={client.appointment_history}
                                    client={client}
                                />
                            )}
                        <div className={styles.notesCard}>
                            <p className={styles.notesTitle}>All Notes</p>
                            <div className={styles.notesList}>
                                {client.status ===
                                    ClientStatus.INCOMPLETE_INQUIRY && (
                                    <>
                                        <TextArea
                                            label="Follow-up notes for Incomplete Inquiry"
                                            value={
                                                client[
                                                    ClientStatus.INCOMPLETE_INQUIRY
                                                ]?.notes_for_incomplete_inquiry || ""
                                            }
                                            onChange={(e) =>
                                                updateClientByStatus(
                                                    ClientStatus.INCOMPLETE_INQUIRY,
                                                    "notes_for_incomplete_inquiry",
                                                    e.target.value
                                                )
                                            }
                                            className={styles.notesTextarea}
                                        />
                                    </>
                                )}

                                {(client[ClientStatus.INS_FOLLOWUP]
                                    ?.notes_from_intake_team ||
                                    canEditInsuranceFollowupNotes) &&
                                    activeKey === ClientStatus.INS_VERIFICATION && (
                                        <>
                                            <TextArea
                                                label="Follow-up Response from Intake"
                                                value={
                                                    client[ClientStatus.INS_FOLLOWUP]
                                                        ?.notes_from_intake_team ||
                                                    ""
                                                }
                                                onChange={(e) =>
                                                    updateClientByStatus(
                                                        ClientStatus.INS_FOLLOWUP,
                                                        "notes_from_intake_team",
                                                        e.target.value
                                                    )
                                                }
                                                className={styles.notesTextarea}
                                                readOnly={
                                                    !canEditInsuranceFollowupNotes
                                                }
                                            />
                                        </>
                                    )}

                                {(activeKey === ClientStatus.INS_VERIFICATION ||
                                    activeKey === ClientStatus.INS_FOLLOWUP ||
                                    activeKey === ClientStatus.INQUIRY ||
                                    activeKey ===
                                        ClientStatus.INCOMPLETE_INQUIRY) && (
                                    <TextArea
                                        label="Insurance verification request notes"
                                        value={
                                            client[ClientStatus.INS_VERIFICATION]
                                                ?.ins_request_notes || ""
                                        }
                                        onChange={(e) => {
                                            updateClientByStatus(
                                                ClientStatus.INS_VERIFICATION,
                                                "ins_request_notes",
                                                e.target.value
                                            );
                                        }}
                                        className={styles.notesTextarea}
                                    />
                                )}

                                {client.status === ClientStatus.INS_VERIFICATION && (
                                    <>
                                        <TextArea
                                            label="Follow-up Request from Insurance"
                                            value={
                                                client[ClientStatus.INS_FOLLOWUP]
                                                    ?.notes_from_ins_team || ""
                                            }
                                            onChange={(e) => {
                                                updateClientByStatus(
                                                    ClientStatus.INS_FOLLOWUP,
                                                    "notes_from_ins_team",
                                                    e.target.value
                                                );
                                            }}
                                            className={styles.notesTextarea}
                                            errorText="Please fill this out if you'd like to request follow-up"
                                            errorTextClassName={styles.errorText}
                                        />
                                    </>
                                )}

                                {(activeKey === ClientStatus.APPT_CONFIRMED ||
                                    activeKey === ClientStatus.APPT_HELD) && (
                                    <TextArea
                                        label="Call Notes"
                                        value={
                                            client[ClientStatus.APPT_SETTING]
                                                ?.call_notes
                                        }
                                        onChange={(e) => {
                                            updateClientByStatus(
                                                ClientStatus.APPT_SETTING,
                                                "call_notes",
                                                e.target.value
                                            );
                                        }}
                                        className={styles.notesTextarea}
                                        errorText="Notes from the call with the client"
                                        errorTextClassName={styles.errorText}
                                    />
                                )}
                                {(activeKey === ClientStatus.APPT_CONFIRMED ||
                                    activeKey === ClientStatus.APPT_HELD) && (
                                    <TextArea
                                        label="Email Notes"
                                        value={
                                            client[ClientStatus.APPT_SETTING]
                                                ?.email_notes
                                        }
                                        onChange={(e) => {
                                            updateClientByStatus(
                                                ClientStatus.APPT_SETTING,
                                                "email_notes",
                                                e.target.value
                                            );
                                        }}
                                        className={styles.notesTextarea}
                                        errorText="Refined notes to be sent to the clinician along with the appointment confirmation notification"
                                        errorTextClassName={styles.errorText}
                                    />
                                )}
                                {client.status === ClientStatus.INS_FOLLOWUP && (
                                    <>
                                        <p className={styles.followupNotesTitle}>
                                            Insurance Follow-up Notes
                                        </p>
                                        <TextArea
                                            label="Follow-up Request from Insurance"
                                            value={
                                                client[ClientStatus.INS_FOLLOWUP]
                                                    ?.notes_from_ins_team || ""
                                            }
                                            className={styles.notesTextarea}
                                            readOnly
                                        />
                                        <TextArea
                                            label="Follow-up Response from Intake"
                                            value={
                                                client[ClientStatus.INS_FOLLOWUP]
                                                    ?.notes_from_intake_team || ""
                                            }
                                            onChange={(e) =>
                                                updateClientByStatus(
                                                    ClientStatus.INS_FOLLOWUP,
                                                    "notes_from_intake_team",
                                                    e.target.value
                                                )
                                            }
                                            className={styles.notesTextarea}
                                            readOnly={!canEditInsuranceFollowupNotes}
                                        />
                                        <p className={styles.noteText}>
                                            Day 1 - Send email from template <br />
                                            Day 2 - if no reply - call client, alert
                                            to email sent the prior day; leave
                                            voicemail if no answer.
                                        </p>
                                    </>
                                )}

                                {activeKey === ClientStatus.FOLLOWUP && (
                                    <TextArea
                                        label="Follow ups"
                                        value={
                                            client[ClientStatus.FOLLOWUP]
                                                ?.followup_notes || ""
                                        }
                                        onChange={(e) =>
                                            updateClientByStatus(
                                                ClientStatus.FOLLOWUP,
                                                "followup_notes",
                                                e.target.value
                                            )
                                        }
                                        className={styles.notesTextarea}
                                    />
                                )}
                                {activeKey === ClientStatus.APPT_SETTING && (
                                    <TextArea
                                        label="Follow ups"
                                        value={
                                            client[ClientStatus.APPT_SETTING]
                                                ?.follow_ups || ""
                                        }
                                        onChange={(e) => {
                                            updateClientByStatus(
                                                ClientStatus.APPT_SETTING,
                                                "follow_ups",
                                                e.target.value
                                            );
                                        }}
                                    />
                                )}
                                <TextArea
                                    label="General Notes"
                                    value={client.notes || ""}
                                    onChange={(e) =>
                                        updateClient("notes", e.target.value)
                                    }
                                    className={styles.notesTextarea}
                                />
                                {(userAcl.billing ||
                                    userAcl.super_admin ||
                                    userAcl.intake) && (
                                    <TextArea
                                        label="Client Services Notes"
                                        value={client.client_services_notes || ""}
                                        onChange={(e) =>
                                            updateClient(
                                                "client_services_notes",
                                                e.target.value
                                            )
                                        }
                                        className={styles.notesTextarea}
                                    />
                                )}
                            </div>
                        </div>
                        {activeKey === ClientStatus.APPT_SETTING &&
                            client.intake_session_history.length > 0 && (
                                <IntakeSessionHistory
                                    initialHistories={client.intake_session_history}
                                    client={client}
                                />
                            )}
                    </div>
                )}
            </div>
        </fieldset>
    );
}
